// src/pages/Clientes.jsx
import Cargando from "../components/Cargando";
import React, { useState, useMemo, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useClientes } from "../hooks/useClientes";
import { useCotizaciones } from "../hooks/useCotizaciones";
import { enviarWhatsApp } from "../utils/emailService";
import * as XLSX from "xlsx";

const ESTADO_CLI_STYLE = {
  "Nuevo":       { bg: "#E6F1FB", color: "#185FA5" },
  "Contactado":  { bg: "#EEEDFE", color: "#534AB7" },
  "Propuesta":   { bg: "#FAEEDA", color: "#854F0B" },
  "Negociación": { bg: "#EAF3DE", color: "#3B6D11" },
  "Cerrado":     { bg: "#E1F5EE", color: "#0F6E56" },
  "Perdido":     { bg: "#FCEBEB", color: "#A32D2D" },
};

const PRIORIDAD_STYLE = {
  "1 - Baja":  { bg: "#f1f5f9", color: "#64748b" },
  "2":         { bg: "#EEF2FF", color: "#4338CA" },
  "3 - Media": { bg: "#FEF9C3", color: "#854F0B" },
  "4":         { bg: "#FEF3C7", color: "#92400E" },
  "5 - Alta":  { bg: "#FCEBEB", color: "#A32D2D" },
};

const COT_ESTADO_STYLE = {
  "Nueva":       { bg: "#E6F1FB", color: "#185FA5" },
  "Contactado":  { bg: "#EEEDFE", color: "#534AB7" },
  "Propuesta":   { bg: "#FAEEDA", color: "#854F0B" },
  "Negociación": { bg: "#EAF3DE", color: "#3B6D11" },
  "Cerrada":     { bg: "#E1F5EE", color: "#0F6E56" },
  "Perdida":     { bg: "#FCEBEB", color: "#A32D2D" },
};

function SortIcon({ field, sortField, sortDir }) {
  if (sortField !== field) return <span style={{ color: "#cbd5e1", fontSize: 9 }}> ⇅</span>;
  return <span style={{ color: "#3A3C7A", fontSize: 9 }}>{sortDir === "asc" ? " ↑" : " ↓"}</span>;
}

const TH_SORT = {
  textAlign: "left", fontSize: 11, fontWeight: 600, color: "#94a3b8",
  padding: "10px 14px", whiteSpace: "nowrap", cursor: "pointer", userSelect: "none",
};
const TH = {
  textAlign: "left", fontSize: 11, fontWeight: 600, color: "#94a3b8",
  padding: "10px 14px", whiteSpace: "nowrap",
};

export default function Clientes() {
  const { clientes, loading, eliminarCliente } = useClientes();
  const { cotizaciones } = useCotizaciones();
  const navigate = useNavigate();

  const [busqueda,          setBusqueda]          = useState("");
  const [filtroTipo,        setFiltroTipo]        = useState("Todos");
  const [filtroEstado,      setFiltroEstado]      = useState("Todos");
  const [filtroResponsable, setFiltroResponsable] = useState("Todos");
  const [filtroSeguim,      setFiltroSeguim]      = useState("Todos");
  const [pagina,            setPagina]            = useState(1);
  const [confirmDel,        setConfirmDel]        = useState(null);
  const [expandedId,        setExpandedId]        = useState(null);
  const [sortField,         setSortField]         = useState("creadoEn");
  const [sortDir,           setSortDir]           = useState("desc");

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  // Cotizaciones agrupadas por clienteId
  const cotPorCliente = useMemo(() => {
    const map = {};
    cotizaciones.forEach(cot => {
      if (!cot.clienteId) return;
      if (!map[cot.clienteId]) map[cot.clienteId] = [];
      map[cot.clienteId].push(cot);
    });
    return map;
  }, [cotizaciones]);

  // Ingresos reales por cliente (cotizaciones cerradas)
  const ingresosPorCliente = useMemo(() => {
    const map = {};
    cotizaciones.forEach(c => {
      if (!c.clienteId) return;
      if (!map[c.clienteId]) map[c.clienteId] = 0;
      if (["Ganada", "Cerrada"].includes(c.estado)) map[c.clienteId] += Number(c.valor) || 0;
    });
    return map;
  }, [cotizaciones]);

  // Clientes enriquecidos con tipo, conteos e ingresos
  const clientesEnriquecidos = useMemo(() => (
    clientes.map(c => ({
      ...c,
      tipo:            (cotPorCliente[c.id]?.length || 0) > 0 ? "Activo" : "Prospecto",
      numCotizaciones: cotPorCliente[c.id]?.length || 0,
      ingresos:        ingresosPorCliente[c.id] || 0,
    }))
  ), [clientes, cotPorCliente, ingresosPorCliente]);

  // Responsables únicos para el filtro
  const responsables = useMemo(() => {
    const set = new Set(clientes.map(c => c.responsable).filter(Boolean));
    return [...set].sort();
  }, [clientes]);

  // Filtrado
  const filtrados = useMemo(() => {
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    const en7 = new Date(hoy); en7.setDate(en7.getDate() + 7);
    function parsearLocal(str) {
      if (!str) return null;
      const [y, m, d] = String(str).split("-").map(Number);
      return new Date(y, m - 1, d);
    }
    return clientesEnriquecidos.filter(c => {
      const q = busqueda.toLowerCase();
      const matchBusqueda = !busqueda ||
        c.nombre?.toLowerCase().includes(q) ||
        c.empresa?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.ruc?.toLowerCase().includes(q) ||
        c.telefono?.includes(busqueda);
      const matchTipo  = filtroTipo === "Todos" || c.tipo === filtroTipo;
      const matchEstado = filtroEstado === "Todos" || c.estado === filtroEstado;
      const matchResp  = filtroResponsable === "Todos" || c.responsable === filtroResponsable;
      let matchSeguim  = true;
      if (filtroSeguim === "Vencidos") {
        const f = parsearLocal(c.proximoSeguimiento);
        matchSeguim = !!(f && f < hoy);
      } else if (filtroSeguim === "Próximos 7d") {
        const f = parsearLocal(c.proximoSeguimiento);
        matchSeguim = !!(f && f >= hoy && f <= en7);
      }
      return matchBusqueda && matchTipo && matchEstado && matchResp && matchSeguim;
    });
  }, [clientesEnriquecidos, busqueda, filtroTipo, filtroEstado, filtroResponsable, filtroSeguim]);

  // Ordenado
  const filas = useMemo(() => (
    [...filtrados].sort((a, b) => {
      let va, vb;
      if      (sortField === "empresa")     { va = a.empresa?.toLowerCase() || ""; vb = b.empresa?.toLowerCase() || ""; }
      else if (sortField === "valor")        { va = Number(a.valor) || 0;   vb = Number(b.valor) || 0; }
      else if (sortField === "ingresos")     { va = a.ingresos;             vb = b.ingresos; }
      else if (sortField === "seguimiento")  { va = a.proximoSeguimiento || "9999"; vb = b.proximoSeguimiento || "9999"; }
      else if (sortField === "creadoEn")     { const fa = a.creadoEn?.toDate ? a.creadoEn.toDate() : new Date(a.creadoEn || 0); const fb = b.creadoEn?.toDate ? b.creadoEn.toDate() : new Date(b.creadoEn || 0); va = fa.getTime(); vb = fb.getTime(); }
      else                                   { va = a.nombre?.toLowerCase() || ""; vb = b.nombre?.toLowerCase() || ""; }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    })
  ), [filtrados, sortField, sortDir]);

  const ESTADOS_CLI = ["Nuevo", "Contactado", "Propuesta", "Negociación", "Cerrado", "Perdido"];

  // KPIs
  const totalIngresos = Object.values(ingresosPorCliente).reduce((s, v) => s + v, 0);

  const kpisLocal = useMemo(() => {
    const porEstado = {}; const porEstadoValor = {};
    ESTADOS_CLI.forEach(e => { porEstado[e] = 0; porEstadoValor[e] = 0; });
    clientesEnriquecidos.forEach(c => {
      const e = c.estado || "Nuevo";
      if (e in porEstado) { porEstado[e]++; porEstadoValor[e] += Number(c.valor) || 0; }
    });
    const pipelineActivo = ESTADOS_CLI
      .filter(e => !["Cerrado", "Perdido"].includes(e))
      .reduce((s, e) => s + porEstadoValor[e], 0);
    const totalValor = clientesEnriquecidos.reduce((s, c) => s + (Number(c.valor) || 0), 0);
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    const conSeguimVencido = clientesEnriquecidos.filter(c => {
      if (!c.proximoSeguimiento) return false;
      const [y, m, d] = String(c.proximoSeguimiento).split("-").map(Number);
      return new Date(y, m - 1, d) < hoy;
    }).length;
    return { porEstado, porEstadoValor, pipelineActivo, totalValor, conSeguimVencido };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientesEnriquecidos]);

  // Exportar Excel
  const exportarExcel = () => {
    const data = filas.map(c => ({
      "Nombre":                 c.nombre || "",
      "Empresa":                c.empresa || "",
      "RUC/DNI":                c.ruc || "",
      "Teléfono":               c.telefono || "",
      "Email":                  c.email || "",
      "Ciudad":                 c.ciudad || "",
      "Estado":                 c.estado || "",
      "Fuente":                 c.fuente || "",
      "Prioridad":              c.prioridad || "",
      "Valor potencial (S/)":   Number(c.valor) || 0,
      "Ingresos generados (S/)": c.ingresos,
      "Cotizaciones":           c.numCotizaciones,
      "Responsable":            c.responsable || "",
      "Próx. seguimiento":      c.proximoSeguimiento || "",
      "Notas":                  c.notas || "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = Array(15).fill({ wch: 22 });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Clientes");
    XLSX.writeFile(wb, `Clientes_${new Date().toLocaleDateString("es-PE").replace(/\//g, "-")}.xlsx`);
  };

  const hayFiltros = busqueda || filtroTipo !== "Todos" || filtroEstado !== "Todos" || filtroResponsable !== "Todos" || filtroSeguim !== "Todos";

  const POR_PAGINA    = 25;
  const totalPaginas  = Math.max(1, Math.ceil(filas.length / POR_PAGINA));
  // Clamp: si el filtro reduce resultados y pagina queda fuera de rango, vuelve a la 1
  const paginaValida  = Math.min(pagina, totalPaginas);
  const paginadas     = filas.slice((paginaValida - 1) * POR_PAGINA, paginaValida * POR_PAGINA);

  useEffect(() => { setPagina(1); }, [busqueda, filtroTipo, filtroEstado, filtroResponsable, filtroSeguim]);

  if (loading) return (
    <Cargando />
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#1e293b" }}>
          Clientes <span style={{ color: "#94a3b8", fontWeight: 400, fontSize: 18 }}>({filas.length})</span>
        </h1>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={exportarExcel}
            style={{ background: "#fff", color: "#1D9E75", border: "1.5px solid #1D9E75", fontSize: 13, fontWeight: 600, padding: "8px 14px", borderRadius: 8, cursor: "pointer" }}
          >
            ↓ Excel
          </button>
          <Link to="/clientes/nuevo" style={{
            background: "#F97316", color: "#000", textDecoration: "none",
            fontSize: 13, fontWeight: 700, padding: "8px 18px", borderRadius: 8,
          }}>
            + Nuevo cliente
          </Link>
        </div>
      </div>

      {/* Micro-interacciones Emil Kowalski */}
      <style>{`
        .cli-chip { transition: background 150ms cubic-bezier(0.23,1,0.32,1), box-shadow 150ms cubic-bezier(0.23,1,0.32,1), transform 100ms cubic-bezier(0.23,1,0.32,1); }
        .cli-chip:active { transform: scale(0.96) !important; }
        .cli-chip:hover  { filter: brightness(0.95); }
        .cli-kpi { transition: box-shadow 200ms cubic-bezier(0.23,1,0.32,1), transform 200ms cubic-bezier(0.23,1,0.32,1); }
        .cli-kpi:hover { box-shadow: 0 6px 20px rgba(58,60,122,0.10); transform: translateY(-1px); }
      `}</style>

      {/* Fila 1 — Estado chips: filtro + KPI combinado */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8 }}>
        {/* Chip Todos */}
        {(() => {
          const activo = filtroEstado === "Todos";
          return (
            <button className="cli-chip" onClick={() => { setFiltroEstado("Todos"); setPagina(1); }} style={{
              background: activo ? "#3A3C7A" : "#fff",
              color: activo ? "#fff" : "#475569",
              border: `1.5px solid ${activo ? "#3A3C7A" : "#e2e8f0"}`,
              borderRadius: 12, padding: "12px 6px", cursor: "pointer", textAlign: "center",
              boxShadow: activo ? "0 2px 10px rgba(58,60,122,0.28)" : "none",
            }}>
              <div style={{ fontSize: 9, fontWeight: 700, opacity: 0.75, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.05em" }}>Todos</div>
              <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1 }}>{clientes.length}</div>
              <div style={{ fontSize: 9, marginTop: 4, opacity: 0.7 }}>S/ {kpisLocal.totalValor.toLocaleString("es-PE")}</div>
            </button>
          );
        })()}
        {/* Chips por estado */}
        {ESTADOS_CLI.map(e => {
          const s = ESTADO_CLI_STYLE[e];
          const activo = filtroEstado === e;
          return (
            <button key={e} className="cli-chip" onClick={() => { setFiltroEstado(e); setPagina(1); }} style={{
              background: activo ? s.color : s.bg,
              color: activo ? "#fff" : s.color,
              border: `1.5px solid ${activo ? s.color : "transparent"}`,
              borderRadius: 12, padding: "12px 6px", cursor: "pointer", textAlign: "center",
              boxShadow: activo ? `0 2px 10px ${s.color}44` : "none",
            }}>
              <div style={{ fontSize: 9, fontWeight: 700, opacity: activo ? 0.9 : 0.75, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.04em" }}>{e}</div>
              <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1, marginTop: 2 }}>{kpisLocal.porEstado[e] || 0}</div>
              <div style={{ fontSize: 9, marginTop: 4, opacity: activo ? 0.9 : 0.7 }}>S/ {(kpisLocal.porEstadoValor[e] || 0).toLocaleString("es-PE")}</div>
            </button>
          );
        })}
      </div>

      {/* Fila 2 — Métricas de performance */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
        {[
          { label: "Pipeline activo",      valor: `S/ ${kpisLocal.pipelineActivo.toLocaleString("es-PE")}`, sub: `${(kpisLocal.porEstado["Nuevo"]||0)+(kpisLocal.porEstado["Contactado"]||0)+(kpisLocal.porEstado["Propuesta"]||0)+(kpisLocal.porEstado["Negociación"]||0)} clientes en proceso`, color: "#8B5CF6" },
          { label: "Ingresos reales",      valor: `S/ ${totalIngresos.toLocaleString("es-PE")}`,            sub: "de cotizaciones ganadas",                                       color: "#1D9E75" },
          { label: "Seguim. vencidos",     valor: kpisLocal.conSeguimVencido,                               sub: kpisLocal.conSeguimVencido > 0 ? "requieren atención urgente" : "todos al día", color: kpisLocal.conSeguimVencido > 0 ? "#DC2626" : "#1D9E75" },
        ].map(k => (
          <div key={k.label} className="cli-kpi" style={{ background: "#fff", border: "0.5px solid #e8ecf3", borderTop: `3px solid ${k.color}`, borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ fontSize: 10, color: "#64748b", fontWeight: 500, marginBottom: 4 }}>{k.label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#1e293b", letterSpacing: "-0.4px" }}>{k.valor}</div>
            <div style={{ fontSize: 10, marginTop: 4, color: k.color, fontWeight: 500 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Filtros — búsqueda + tipo + responsable + seguimiento */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <input
          type="text"
          placeholder="Buscar nombre, empresa, RUC, teléfono, email..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          style={{ flex: 1, minWidth: 220, padding: "8px 14px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, outline: "none", color: "#1e293b" }}
        />
        <div style={{ display: "flex", gap: 3 }}>
          {["Todos", "Activo", "Prospecto"].map(t => (
            <button key={t} onClick={() => setFiltroTipo(t)} style={{
              padding: "6px 12px", borderRadius: 8, fontSize: 11, fontWeight: 500,
              border: "none", cursor: "pointer",
              background: filtroTipo === t ? "#3A3C7A" : "#f1f5f9",
              color:      filtroTipo === t ? "#fff"    : "#64748b",
            }}>{t}</button>
          ))}
        </div>
        {responsables.length > 0 && (
          <select value={filtroResponsable} onChange={e => setFiltroResponsable(e.target.value)} style={selStyle}>
            <option value="Todos">Responsable: Todos</option>
            {responsables.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        )}
        <select value={filtroSeguim} onChange={e => setFiltroSeguim(e.target.value)} style={selStyle}>
          <option value="Todos">Seguim.: Todos</option>
          <option value="Vencidos">⚠ Vencidos</option>
          <option value="Próximos 7d">📅 Próximos 7d</option>
        </select>
        {hayFiltros && (
          <button
            onClick={() => { setBusqueda(""); setFiltroTipo("Todos"); setFiltroEstado("Todos"); setFiltroResponsable("Todos"); setFiltroSeguim("Todos"); setPagina(1); }}
            style={{ padding: "6px 12px", border: "none", borderRadius: 8, fontSize: 11, color: "#DC2626", background: "#FCEBEB", cursor: "pointer", fontWeight: 600 }}
          >
            × Limpiar filtros
          </button>
        )}
      </div>

      {/* Tabla */}
      <div style={{ background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
              <tr>
                <th onClick={() => handleSort("nombre")}  style={TH_SORT}>Cliente <SortIcon field="nombre" sortField={sortField} sortDir={sortDir} /></th>
                <th onClick={() => handleSort("empresa")} style={TH_SORT}>Empresa <SortIcon field="empresa" sortField={sortField} sortDir={sortDir} /></th>
                <th style={TH}>Estado / Prior.</th>
                <th style={TH}>Teléfono</th>
                <th onClick={() => handleSort("valor")}   style={TH_SORT}>Valor / Ingresos <SortIcon field="valor" sortField={sortField} sortDir={sortDir} /></th>
                <th onClick={() => handleSort("seguimiento")} style={TH_SORT}>Seguim. <SortIcon field="seguimiento" sortField={sortField} sortDir={sortDir} /></th>
                <th style={TH}>Responsable</th>
                <th style={TH}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filas.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>
                    No se encontraron clientes
                  </td>
                </tr>
              ) : paginadas.map(c => {
                const estiloEst = ESTADO_CLI_STYLE[c.estado] || ESTADO_CLI_STYLE["Nuevo"];
                const estiloP   = PRIORIDAD_STYLE[c.prioridad] || PRIORIDAD_STYLE["3 - Media"];
                const vencido   = c.proximoSeguimiento && (() => { const [y,m,d]=String(c.proximoSeguimiento).split("-").map(Number); const hoyMs=new Date(); hoyMs.setHours(0,0,0,0); return new Date(y,m-1,d) < hoyMs; })();
                const isExp     = expandedId === c.id;
                const cotsCli   = cotPorCliente[c.id] || [];

                return (
                  <React.Fragment key={c.id}>

                    {/* Fila principal */}
                    <tr
                      style={{ borderBottom: "0.5px solid #f1f5f9", cursor: "pointer", background: isExp ? "#f8fafc" : "transparent" }}
                      onClick={() => setExpandedId(isExp ? null : c.id)}
                    >
                      {/* Cliente */}
                      <td style={{ padding: "10px 14px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{
                            width: 28, height: 28, borderRadius: "50%",
                            background: "#E6F1FB", color: "#185FA5",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 11, fontWeight: 700, flexShrink: 0,
                          }}>
                            {c.nombre?.charAt(0)}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, color: "#1e293b" }}>{c.nombre}</div>
                            <div style={{ fontSize: 11, color: "#94a3b8" }}>{c.email}</div>
                          </div>
                        </div>
                      </td>

                      {/* Empresa */}
                      <td style={{ padding: "10px 14px" }}>
                        <div style={{ color: "#475569" }}>{c.empresa || "—"}</div>
                        {c.proyecto && <div style={{ fontSize: 10, color: "#94a3b8" }}>{c.proyecto}</div>}
                      </td>

                      {/* Estado / Prioridad */}
                      <td style={{ padding: "10px 14px" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 8, background: estiloEst.bg, color: estiloEst.color, width: "fit-content" }}>
                            {c.estado || "Nuevo"}
                          </span>
                          {c.prioridad && (
                            <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 8, background: estiloP.bg, color: estiloP.color, width: "fit-content" }}>
                              {c.prioridad}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Teléfono */}
                      <td style={{ padding: "10px 14px" }}>
                        {c.telefono ? (
                          <button
                            onClick={e => { e.stopPropagation(); enviarWhatsApp(c.telefono, `Hola ${c.nombre}, `); }}
                            style={{ background: "#E1F5EE", color: "#0F6E56", border: "none", borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontWeight: 600 }}
                          >
                            📱 {c.telefono}
                          </button>
                        ) : <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>}
                      </td>

                      {/* Valor / Ingresos */}
                      <td style={{ padding: "10px 14px" }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>
                          S/ {Number(c.valor || 0).toLocaleString("es-PE")}
                        </div>
                        {c.ingresos > 0 && (
                          <div style={{ fontSize: 10, color: "#1D9E75", fontWeight: 600 }}>
                            ✓ S/ {c.ingresos.toLocaleString("es-PE")}
                          </div>
                        )}
                        <div style={{ fontSize: 10, color: "#94a3b8" }}>
                          {c.numCotizaciones > 0 ? `${c.numCotizaciones} cot.` : "sin cot."}
                        </div>
                      </td>

                      {/* Seguimiento */}
                      <td style={{ padding: "10px 14px" }}>
                        {c.proximoSeguimiento ? (
                          <span style={{
                            fontSize: 11, fontWeight: 600,
                            color: vencido ? "#DC2626" : "#475569",
                            background: vencido ? "#FCEBEB" : "transparent",
                            padding: vencido ? "2px 6px" : 0,
                            borderRadius: vencido ? 6 : 0,
                          }}>
                            {vencido ? "⚠ " : ""}{c.proximoSeguimiento}
                          </span>
                        ) : <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>}
                      </td>

                      {/* Responsable */}
                      <td style={{ padding: "10px 14px", color: "#64748b", fontSize: 12 }}>
                        {c.responsable || "—"}
                      </td>

                      {/* Acciones */}
                      <td style={{ padding: "10px 14px" }}>
                        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                          <button
                            onClick={e => { e.stopPropagation(); navigate(`/cotizaciones?clienteId=${c.id}&clienteNombre=${encodeURIComponent(c.nombre)}&clienteEmpresa=${encodeURIComponent(c.empresa||"")}&responsable=${encodeURIComponent(c.responsable||"")}`); }}
                            style={{ fontSize: 11, background: "#E1F5EE", color: "#0F6E56", padding: "4px 10px", borderRadius: 6, border: "none", cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}
                          >
                            + Cot.
                          </button>
                          <Link
                            to={`/clientes/${c.id}/perfil`}
                            onClick={e => e.stopPropagation()}
                            style={{ fontSize: 11, background: "#EDE9FE", color: "#7C3AED", padding: "4px 10px", borderRadius: 6, textDecoration: "none", fontWeight: 600 }}
                          >
                            360°
                          </Link>
                          <Link
                            to={`/clientes/${c.id}`}
                            onClick={e => e.stopPropagation()}
                            style={{ fontSize: 11, background: "#E6F1FB", color: "#185FA5", padding: "4px 10px", borderRadius: 6, textDecoration: "none", fontWeight: 600 }}
                          >
                            Editar
                          </Link>
                          {confirmDel === c.id ? (
                            <div style={{ display: "flex", gap: 4 }} onClick={e => e.stopPropagation()}>
                              <button onClick={() => { eliminarCliente(c.id); setConfirmDel(null); }} style={{ fontSize: 11, background: "#DC2626", color: "#fff", padding: "4px 8px", borderRadius: 6, border: "none", cursor: "pointer", fontWeight: 600 }}>Sí</button>
                              <button onClick={() => setConfirmDel(null)} style={{ fontSize: 11, background: "#f1f5f9", color: "#475569", padding: "4px 8px", borderRadius: 6, border: "none", cursor: "pointer" }}>No</button>
                            </div>
                          ) : (
                            <button
                              onClick={e => { e.stopPropagation(); setConfirmDel(c.id); }}
                              style={{ fontSize: 11, background: "#FCEBEB", color: "#A32D2D", padding: "4px 10px", borderRadius: 6, border: "none", cursor: "pointer", fontWeight: 600 }}
                            >
                              Del
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Fila expandida — detalle del cliente */}
                    {isExp && (
                      <tr>
                        <td colSpan={8} style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", padding: 0 }}>
                          <div style={{ padding: "14px 20px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>

                            {/* Datos adicionales */}
                            <div>
                              <div style={{ fontSize: 10, fontWeight: 700, color: "#3A3C7A", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                                Datos del cliente
                              </div>
                              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "5px 16px" }}>
                                {c.ruc      && <Row label="RUC/DNI"   val={c.ruc} />}
                                {c.ciudad   && <Row label="Ciudad"    val={c.ciudad} />}
                                {c.fuente   && <Row label="Fuente"    val={c.fuente} />}
                                {c.prioridad && <Row label="Prioridad" val={c.prioridad} />}
                                {c.direccion && <Row label="Dirección" val={c.direccion} full />}
                                {c.notas    && <Row label="Notas"     val={c.notas} full muted />}
                              </div>
                            </div>

                            {/* Cotizaciones del cliente */}
                            <div>
                              <div style={{ fontSize: 10, fontWeight: 700, color: "#3A3C7A", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                                Cotizaciones ({cotsCli.length})
                              </div>
                              {cotsCli.length === 0 ? (
                                <p style={{ fontSize: 12, color: "#94a3b8" }}>Sin cotizaciones aún.</p>
                              ) : (
                                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                  {cotsCli.slice(0, 5).map(cot => {
                                    const ec = COT_ESTADO_STYLE[cot.estado] || COT_ESTADO_STYLE["Nueva"];
                                    return (
                                      <div key={cot.id} style={{
                                        display: "flex", alignItems: "center", justifyContent: "space-between",
                                        background: "#fff", borderRadius: 7, padding: "6px 10px", border: "0.5px solid #e2e8f0",
                                      }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                          {cot.numeroCot && (
                                            <span style={{ fontSize: 9, fontWeight: 700, color: "#3A3C7A", background: "#EEF2FF", padding: "1px 5px", borderRadius: 4 }}>
                                              {cot.numeroCot}
                                            </span>
                                          )}
                                          <span style={{ fontSize: 11, color: "#475569" }}>{cot.proyecto || "Sin proyecto"}</span>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                          <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 6, background: ec.bg, color: ec.color }}>{cot.estado}</span>
                                          <span style={{ fontSize: 11, fontWeight: 700, color: "#1e293b" }}>S/ {Number(cot.valor || 0).toLocaleString("es-PE")}</span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                  {cotsCli.length > 5 && (
                                    <div style={{ fontSize: 11, color: "#94a3b8", textAlign: "center" }}>
                                      + {cotsCli.length - 5} más
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                          </div>
                        </td>
                      </tr>
                    )}

                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <BarraPaginas total={filas.length} porPagina={POR_PAGINA} pagina={paginaValida} onChange={setPagina} />
      </div>

    </div>
  );
}

function BarraPaginas({ total, porPagina, pagina, onChange }) {
  if (!total || total <= porPagina) return null;
  const totalPags = Math.ceil(total / porPagina);
  const desde = (pagina - 1) * porPagina + 1;
  const hasta  = Math.min(pagina * porPagina, total);
  const nums = [];
  for (let i = 1; i <= totalPags; i++) {
    if (i === 1 || i === totalPags || (i >= pagina - 1 && i <= pagina + 1)) nums.push(i);
    else if (nums[nums.length - 1] !== "...") nums.push("...");
  }
  const btnBase = { padding: "5px 12px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", fontSize: 12, fontWeight: 500, cursor: "pointer" };
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderTop: "1px solid #f1f5f9" }}>
      <span style={{ fontSize: 12, color: "#64748b" }}>Mostrando {desde} a {hasta} de {total} registros</span>
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <button onClick={() => onChange(pagina - 1)} disabled={pagina === 1}
          style={{ ...btnBase, color: pagina === 1 ? "#d1d5db" : "#475569", cursor: pagina === 1 ? "not-allowed" : "pointer" }}>
          Anterior
        </button>
        {nums.map((n, i) => n === "..." ? (
          <span key={`e${i}`} style={{ fontSize: 12, color: "#94a3b8", padding: "0 2px" }}>…</span>
        ) : (
          <button key={n} onClick={() => onChange(n)} style={{
            width: 32, height: 32, borderRadius: 6, border: "1px solid", fontSize: 12, fontWeight: 600, cursor: "pointer",
            borderColor: pagina === n ? "#3A3C7A" : "#e2e8f0",
            background:  pagina === n ? "#3A3C7A" : "#fff",
            color:       pagina === n ? "#fff"    : "#475569",
          }}>{n}</button>
        ))}
        <button onClick={() => onChange(pagina + 1)} disabled={pagina === totalPags}
          style={{ ...btnBase, color: pagina === totalPags ? "#d1d5db" : "#475569", cursor: pagina === totalPags ? "not-allowed" : "pointer" }}>
          Siguiente
        </button>
      </div>
    </div>
  );
}

// Pequeño helper para las filas del panel expandido
function Row({ label, val, full, muted }) {
  return (
    <div style={full ? { gridColumn: "1 / -1" } : {}}>
      <span style={{ fontSize: 10, color: "#94a3b8" }}>{label}: </span>
      <span style={{ fontSize: 12, color: muted ? "#475569" : "#1e293b" }}>{val}</span>
    </div>
  );
}

const selStyle = {
  padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 8,
  fontSize: 12, color: "#475569", background: "#fff", cursor: "pointer",
};
